<template>
  <div id="groovemail-client-table-view" class="gm-sms-sender">
    <client-table
      class="groovemail-sms-sender-table"
      :tableRef="'integerationsTable'"
      :tableData="integerations"
      :tableColumns="integerationSettingTableColumns"
      :tableOptions="integerationSettingTableOptions"
      :tableCondition="integerations.length"
      :tableTitle="'frontend_groovemail_settings_integeration_settings'"
      :slotData="smsSenderSlotData"
      :emptyStateData="integrationEmptyStateData"
      :addNewBtnText="'frontend_groovemail_segment_new_sender'"
      @addNewData="addIntegeration"
    >
    </client-table>
  </div>
</template>

<script>
import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';

export default {
  name: 'SmsSender',
  props: ['integerations', 'userCanMail'],
  mixins: [demoModeValuesMixin],

  data() {
    return {
      integrationEmptyStateData: {
        emptyStateHeading: 'frontend_groovemail_sender_title',
        emptyStateSubHeading:
          'frontend_groovemail_settings_integeration_dont_have_integeration',
        emptyStateBtnText: 'frontend_groovemail_segment_new_sender',
      },
      integerationSettingTableColumns: [
        'gateway_name',
        'sid',
        'token',
        'phone_number',
        'is_default',
        'actions'
      ],
    };
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    /**
     * Used to return table options for sms sender table.
     * @returns {object} contains table options.
    */
    integerationSettingTableOptions() {
      return {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc'
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query')
        },
        sortable: ['gateway_name'],
        filterable: ['gateway_name', 'sid', 'token', 'from_number'],
        headings: {
          gateway_name: () => this.$t('frontend_groovemail_settings_integeration_settings_gateway_name'),
          sid: () => this.$t('frontend_groovemail_settings_integeration_settings_account_sid'),
          token: () => this.$t('frontend_groovemail_settings_integeration_settings_auth_token'),
          phone_number: () => this.$t('frontend_groovemail_settings_integeration_listing_phone_number'),
          is_default: () => this.$t('frontend_groovemail_settings_integeration_settings_default'),
          actions: () => this.$t('frontend_groovemail_settings_integeration_listing_actions')
        },
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      };
    },

    /**
     * Used to return slot data for sms sender table.
     * @returns {Array} contains slot data.
    */
    smsSenderSlotData() {
      return [
        {
          slotName: 'gateway_name',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicText : props.row.gateway_name,
        },
        {
          slotName: 'sid',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicText : props.row.sid,
        },
        {
          slotName: 'token',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicText : props.row.token,
        },
        {
          slotName: 'phone_number',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicContactText : `${props.row.country_code} ${props.row.from_number}`,
        },
        {
          slotName: 'is_default',
          slotType: 'computed',
          value: props => props.row.is_default === 1 ? this.$t('frontend_yes_I_am_sure') : this.$t('frontend_no'),
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          actionList: [
            {
              id: 0,
              name: 'frontend_edit',
              conditions: () => this.userCanMail !== 0,
              callback: props => this.editIntegeration(props.row.id)
            },
            {
              id: 1,
              name: 'frontend_delete',
              conditions: () => true,
              callback: props => this.removeIntegeration('sms', props.row.id)
            }
          ]
        }
      ];
    }
  },

  components: {
    ClientTable
  },

  methods: {
    // show loader
    showLoader() {
     this.$emit('showLoader');
    },

    // hide loader
    hideLoader() {
      this.$emit('hideLoader');
    },

    // Used to create new sms sender. Redirects user to create sender page if user can mail else displays error message.
    addIntegeration() {
      // show loader
      this.showLoader();

      if (this.userCanMail) {
        this.$router.push({ name: 'CreateIntegeration' });
      } else {
        this.$emit('showError');
      }

      // hide loader
      this.hideLoader();
    },

    /**
     * Used to edit sms-sender. Redirects user to edit sender page if user can mail else displays error message.
     * @param {string} integerationId contains unique id of sms sender.
     */
    editIntegeration(integerationId) {
      // show loader
      this.showLoader();

      if (this.userCanMail) {
        this.$router.push({
          name: 'EditIntegeration',
          params: { id: integerationId }
        });
      } else {
        this.$emit('showError');
      }

      // hide loader
      this.hideLoader();
    },

    /**
     * Emits removeIntegration event to delete sms-sender.
     * @param {string} type contains type of sender.
     * @param {string} id contains unique id of sms sender.
     */
    removeIntegeration(type, id) {
      this.$emit('removeIntegeration', type, id);
    }
  }
};
</script>

<style lang="scss">
.groovemail-sms-sender-table {
  .VueTables__table {
    overflow: auto !important;
  }
  .VueTables__search-field {
    margin-bottom: 85px !important;
  }
  .VueTables__limit {
    bottom: -60px !important;
  }
}
</style>

<style scoped>
.gm-sms-sender,
.gm-sms-sender ::v-deep #gd-client-table-view,
.gm-sms-sender ::v-deep .VueTables--client {
  min-width: 0;
  max-width: 100%;
}

.gm-sms-sender ::v-deep .table-responsive {
  clear: both;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-sms-sender ::v-deep .VueTables__table {
  min-width: 980px;
  table-layout: fixed;
}

.gm-sms-sender ::v-deep .VueTables__table th,
.gm-sms-sender ::v-deep .VueTables__table td {
  vertical-align: middle;
  border-color: var(--gs-border);
}

.gm-sms-sender ::v-deep .VueTables__table th:nth-child(1),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(1) { width: 23%; }
.gm-sms-sender ::v-deep .VueTables__table th:nth-child(2),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(2),
.gm-sms-sender ::v-deep .VueTables__table th:nth-child(3),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(3) { width: 17%; }
.gm-sms-sender ::v-deep .VueTables__table th:nth-child(4),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(4) { width: 18%; }
.gm-sms-sender ::v-deep .VueTables__table th:nth-child(5),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(5) { width: 10%; }
.gm-sms-sender ::v-deep .VueTables__table th:nth-child(6),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(6) { width: 15%; min-width: 148px; white-space: nowrap; }

.gm-sms-sender ::v-deep .VueTables__table td:nth-child(1),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(2),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(3),
.gm-sms-sender ::v-deep .VueTables__table td:nth-child(4) { overflow-wrap: anywhere; }

.gm-sms-sender ::v-deep .VueTables__table td:nth-child(6) .absolute.pin-r > .bg-white > div > div {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
}

.gm-sms-sender ::v-deep .VueTables__table td:nth-child(6) .absolute.pin-r > .bg-white > div:nth-child(2) > div {
  color: var(--gs-danger);
}

@media (max-width: 640px) {
  .gm-sms-sender ::v-deep .new-add-btn-wrapper { position: static; margin-bottom: 16px; }
  .gm-sms-sender ::v-deep .new-add-btn-wrapper > div { flex-wrap: wrap; gap: 12px; }
  .gm-sms-sender ::v-deep .new-add-btn-wrapper > div > div:first-child,
  .gm-sms-sender ::v-deep .new-add-btn-wrapper > div > div:last-child { width: 100%; }
  .gm-sms-sender ::v-deep .new-add-btn-wrapper > div > div:last-child { justify-content: flex-start; }
  .gm-sms-sender ::v-deep .new-add-btn { min-width: 148px; min-height: 44px; }
  .gm-sms-sender ::v-deep #gd-client-table-view .pull-left.VueTables__search { float: none !important; width: 100% !important; margin: 0 !important; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__search-field { width: 100% !important; margin: 0 0 16px !important; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__search-field::before { top: 8px; left: 12px; pointer-events: none; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables .row,
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables .row > .col-md-12 { width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; padding-left: 0 !important; padding-right: 0 !important; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__search__input.form-control { box-sizing: border-box; width: 100% !important; max-width: 100% !important; margin: 0 !important; padding-left: 44px !important; min-height: 44px; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__limit select,
  .gm-sms-sender ::v-deep .VueTables__table td:nth-child(6) .float-right > div[role="button"] > span,
  .gm-sms-sender ::v-deep .VuePagination__pagination-item a { min-height: 44px; }
  .gm-sms-sender ::v-deep .VuePagination__pagination-item a { display: inline-flex; align-items: center; justify-content: center; min-width: 44px; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__limit { position: static !important; float: none !important; bottom: auto !important; margin: 0 10px 16px !important; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__limit-field { position: relative !important; display: inline-flex !important; align-items: center; min-height: 44px; }
  .gm-sms-sender ::v-deep #gd-client-table-view .VueTables__limit-field::after { top: 50% !important; right: 0.75rem !important; transform: translateY(-50%); }
  .gm-sms-sender ::v-deep #gd-client-table-view .VuePagination.row.col-md-12.justify-content-center { clear: both; margin-top: 16px !important; }
  .gm-sms-sender ::v-deep .VueTables__table .VueTables__row:last-child td:nth-child(6) .absolute.pin-r { top: auto; bottom: calc(100% + 8px); margin-top: 0; }
}
</style>
